import React, { useState } from 'react'
import styled from 'styled-components'

import { Box, Icon, PopOver, Text } from 'components'
import Spinner from 'components/Spinner'

import { HexToRgba } from 'utils/colors'
import { Z_INDEX } from 'utils/constants'
import { PopOverProps } from 'components/PopOver/types'

export type MenuDropdownProps = {
  value: string
  icon?: string | Array<string> | React.ReactNode
  iconAlignment?: 'left' | 'right'
  label: string | React.ReactNode
  color?: string
  handleClick?: () => void
  isChecked?: boolean
  iconColor?: string
  isHidden?: boolean
  isDisabled?: boolean
}

type Props = {
  // this is injected by PopOver component
  onToggleOpenMenu?: () => void
  options: MenuDropdownProps[]
  rowWidth: string
  showRowSeparator: boolean
  handleClickOnItem?: (value: string) => void
  isDisabled?: boolean
}

const MoreOptionDropdown: React.FC<Props> = ({
  onToggleOpenMenu,
  options,
  rowWidth,
  handleClickOnItem,
  isDisabled,
  showRowSeparator,
}) => (
  <Container isDisabled={isDisabled}>
    {options &&
      options.map(
        (
          { icon, iconAlignment = 'left', label, color, handleClick, isChecked, value, iconColor, isDisabled = false },
          index,
        ) => (
          <Row
            key={index}
            onClick={() => {
              if (isDisabled) return

              onToggleOpenMenu?.()
              handleClick?.()
              handleClickOnItem?.(value)
            }}
            color={color || ''}
            rowWidth={rowWidth}
            iconAlignment={iconAlignment}
            showRowSeparator={index < options.length - 1 ? showRowSeparator : false}
            isDisabled={isDisabled}
          >
            <Box alignItems="center" display="flex">
              {icon && (typeof icon === 'string' || Array.isArray(icon)) ? (
                <Icon color={iconColor} icon={icon} />
              ) : (
                icon
              )}
              {typeof label === 'string' ? <Text>{label}</Text> : label}
            </Box>
            {isChecked && <Icon color="blue" fontSize={13} icon="check" />}
          </Row>
        ),
      )}
  </Container>
)

type MenuOptions = {
  width?: number
  minWidth?: number
  options: MenuDropdownProps[]
  rowWidth?: string
  icon?: React.ReactNode
  isLoading?: boolean
  showRowSeparator?: boolean
  handleClickOnItem?: (value: string) => void
  onToggleMenu?: (isOpen: boolean) => void
  placement?: PopOverProps['placement']
  isDisabled?: boolean
  absoluteParent?: boolean
  isPortaled?: boolean
}

const MoreOptions: React.FC<MenuOptions> = ({
  icon,
  options,
  placement = 'bottom-end',
  rowWidth = 'auto',
  handleClickOnItem,
  onToggleMenu,
  isDisabled,
  isLoading = false,
  showRowSeparator = false,
  isPortaled = false,
}) => {
  const [openMenu, setOpenMenu] = useState(false)

  const handleToggleMenu = (isOpen: boolean) => {
    setOpenMenu(isOpen)
    onToggleMenu?.(isOpen)
  }

  return (
    <PopOver
      customMenuList={
        <MoreOptionDropdown
          options={options.filter((item) => !item.isHidden)}
          rowWidth={rowWidth}
          handleClickOnItem={handleClickOnItem}
          isDisabled={isDisabled}
          showRowSeparator={showRowSeparator}
        />
      }
      isDisabled={isDisabled}
      onToggleMenu={handleToggleMenu}
      placement={placement}
      isPortaled={isPortaled}
    >
      <IconContainer active={openMenu}>
        {isLoading ? <Spinner fontSize={16} /> : icon ? icon : <Icon icon={['far', 'ellipsis-v']} color={'gray5'} />}
      </IconContainer>
    </PopOver>
  )
}

export default MoreOptions

const IconContainer = styled.div<{ active?: boolean }>`
  position: relative;
  box-sizing: border-box;
  align-items: center;
  background-color: ${(p) => (p.active ? HexToRgba(p.theme.colors.blue1, 0.16) : '')};
  border: solid 1px ${(p) => (p.active ? HexToRgba(p.theme.colors.blue1, 0.16) : 'transparent')};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  height: 24px;
  justify-content: space-evenly;
  width: 24px;
  user-select: none;

  &:hover {
    background: ${(p) => p.theme.colors.blue5};
  }
`

const Container = styled.div<{ isDisabled?: boolean }>`
  opacity: ${(p) => (p.isDisabled ? 0.6 : 1)};
  pointer-events: ${(p) => (p.isDisabled ? 'none' : 'auto')};
`

const Row = styled.div<{
  rowWidth?: string
  iconAlignment?: 'left' | 'right'
  showRowSeparator: boolean
  isDisabled: boolean
}>`
  cursor: ${(p) => (p.isDisabled ? `not-allowed` : `pointer`)};
  display: flex;
  padding: 13px;
  text-align: left;
  user-select: none;
  z-index: ${Z_INDEX.dropdown};
  align-items: center;
  min-height: 16px;
  justify-content: space-between;
  box-sizing: border-box;
  width: ${(p) => p.rowWidth};
  border-bottom: ${(p) => (p.showRowSeparator ? `1px solid ${HexToRgba(p.theme.colors.gray4, 0.8)}` : 'none')};

  div {
    color: ${(p) => (p.isDisabled ? p.theme.colors.gray5 : p.color)};
    display: flex;
    flex-direction: ${(p) => (p.iconAlignment === 'left' ? 'row' : 'row-reverse')};
    align-items: center;
  }

  &:hover {
    background: ${(p) => p.theme.colors.gray1};
  }

  svg {
    ${(p) => (p.iconAlignment === 'left' ? 'padding-right: 10px;' : 'padding-left: 10px;')}
    color: ${(p) => (p.isDisabled ? p.theme.colors.gray5 : p.color)};
  }
`
